import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";

// ==================== Initial State ====================
const initialState = {
  complaintForms: [],
  currentForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all complaint forms (optional patient filter)
export const fetchComplaintForms = createAsyncThunk(
  "complaintForm/fetchAll",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/complaint-form/${patientId}`
        : "/complaint-form";

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch complaint forms"
      );
    }
  }
);

// Fetch single form
export const fetchComplaintForm = createAsyncThunk(
  "complaintForm/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/complaint-form/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch complaint form"
      );
    }
  }
);

// Create complaint form
export const createComplaintForm = createAsyncThunk(
  "complaintForm/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/complaint-form", data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to create complaint form"
      );
    }
  }
);

// Update complaint form (uses id directly)
export const updateComplaintForm = createAsyncThunk(
  "complaintForm/update",
  async ({ case_id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/complaint-form/case/${case_id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to update complaint form"
      );
    }
  }
);

// Delete form
export const deleteComplaintForm = createAsyncThunk(
  "complaintForm/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/complaint-form/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete complaint form"
      );
    }
  }
);

// ==================== Slice ====================
const complaintFormSlice = createSlice({
  name: "complaintForm",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload;
    },
    clearCurrentForm: (state) => {
      state.currentForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchComplaintForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintForms = action.payload;
      })
      .addCase(fetchComplaintForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchComplaintForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentForm = action.payload;
      })
      .addCase(fetchComplaintForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createComplaintForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createComplaintForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintForms.unshift(action.payload);
      })
      .addCase(createComplaintForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateComplaintForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateComplaintForm.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.complaintForms.findIndex(
          (f) => f.id === updated.id
        );
        if (index !== -1) {
          state.complaintForms[index] = updated;
        }

        if (state.currentForm?.id === updated.id) {
          state.currentForm = updated;
        }
      })
      .addCase(updateComplaintForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteComplaintForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteComplaintForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintForms = state.complaintForms.filter(
          (f) => f.id !== action.payload
        );

        if (state.currentForm?.id === action.payload) {
          state.currentForm = null;
        }
      })
      .addCase(deleteComplaintForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentForm, clearCurrentForm } =
  complaintFormSlice.actions;

export default complaintFormSlice.reducer;